import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaPaperPlane, FaCheckCircle } from 'react-icons/fa';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const PageContainer = styled.div`
  padding: 120px 20px 80px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  }
`;

const HeroSection = styled(motion.section)`
  text-align: center;
  margin-bottom: 80px;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: var(--theme-primary);
  margin-bottom: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--theme-hero-start) 0%, var(--theme-hero-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const Subtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const ContactGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 60px;
  margin-top: 60px;
  padding: 0 20px;

  @media (max-width: 1024px) {
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    margin-top: 40px;
  }
`;

const ContactForm = styled(motion.form)`
  background: #ffffff;
  padding: 50px 40px;
  border-radius: 24px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, var(--theme-hero-start), var(--theme-hero-end));
    background-size: 200% 200%;
    animation: ${gradientShift} 3s ease infinite;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 40px;
  text-align: center;
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  color: var(--theme-primary);
  margin-bottom: 12px;
  font-weight: 700;
`;

const FormSubtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  margin: 0;
`;

const FormGroup = styled(motion.div)`
  margin-bottom: 28px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: -0.01em;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fafafa;
  color: #334155;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: var(--theme-accent);
    background: #ffffff;
    box-shadow: 0 0 0 4px var(--theme-accent-soft);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  min-height: 160px;
  resize: vertical;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fafafa;
  color: #334155;
  font-weight: 500;
  line-height: 1.6;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--theme-accent);
    background: #ffffff;
    box-shadow: 0 0 0 4px var(--theme-accent-soft);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--theme-hero-start) 0%, var(--theme-hero-end) 100%);
  color: white;
  padding: 18px 40px;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 25px var(--theme-shadow-accent);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px var(--theme-shadow-accent);
    
    &::before {
      left: 100%;
    }

    svg {
      transform: translateX(4px);
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    transition: transform 0.3s ease;
  }
`;

const ContactInfo = styled(motion.div)`
  background: linear-gradient(135deg, var(--theme-hero-start) 0%, var(--theme-hero-end) 100%);
  padding: 50px 40px;
  border-radius: 24px;
  color: white;
  box-shadow: 
    0 20px 40px var(--theme-shadow-accent),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E");
  }
`;

const InfoHeader = styled.div`
  margin-bottom: 40px;
  position: relative;
  z-index: 2;
`;

const InfoTitle = styled.h2`
  font-size: 1.8rem;
  color: #FFC857;
  margin-bottom: 12px;
  font-weight: 700;
`;

const InfoSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin: 0;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  position: relative;
  z-index: 2;
`;

const InfoItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateX(8px);
  }
`;

const InfoIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(255, 200, 87, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(255, 200, 87, 0.2);

  svg {
    font-size: 1.2rem;
    color: #FFC857;
  }
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoItemTitle = styled.h3`
  color: #FFC857;
  margin-bottom: 8px;
  font-size: 1.1rem;
  font-weight: 600;
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin: 0;
  font-weight: 400;
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, var(--theme-accent), var(--theme-accent-strong));
  color: white;
  padding: 24px;
  border-radius: 16px;
  text-align: center;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-weight: 600;
  box-shadow: 0 8px 25px var(--theme-shadow-accent);
`;

const MapSection = styled(motion.section)`
  margin-top: 80px;
  padding: 0 20px;
`;

const MapContainer = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.03);
  text-align: center;
`;

const MapPlaceholder = styled.div`
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  border-radius: 16px;
  padding: 80px 40px;
  margin-top: 24px;
  color: #64748b;
  font-weight: 500;
  
  svg {
    font-size: 3rem;
    margin-bottom: 20px;
    color: #94a3b8;
  }
`;

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        console.log('Form submitted:', formData);
        setIsSubmitting(false);
        setIsSubmitted(true);
        
        // Reset form after success
        setTimeout(() => {
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: ''
            });
            setIsSubmitted(false);
        }, 5000);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    return (
        <PageContainer>
            <HeroSection
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <Title variants={itemVariants}>
                    Get In Touch
                </Title>
                <Subtitle variants={itemVariants}>
                    Ready to transform your business? Let's discuss your project and explore how 
                    our innovative solutions can drive your success forward.
                </Subtitle>
            </HeroSection>

            <ContactGrid
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <ContactForm onSubmit={handleSubmit} variants={itemVariants}>
                    <FormHeader>
                        <FormTitle>Send us a Message</FormTitle>
                        <FormSubtitle>
                            Fill out the form below and we'll get back to you within 24 hours.
                        </FormSubtitle>
                    </FormHeader>

                    <FormGroup variants={itemVariants}>
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            required
                        />
                    </FormGroup>

                    <FormGroup variants={itemVariants}>
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email address"
                            required
                        />
                    </FormGroup>

                    <FormGroup variants={itemVariants}>
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                            type="text"
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="What is this regarding?"
                            required
                        />
                    </FormGroup>

                    <FormGroup variants={itemVariants}>
                        <Label htmlFor="message">Your Message</Label>
                        <TextArea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your project or inquiry..."
                            required
                        />
                    </FormGroup>

                    {isSubmitted && (
                        <SuccessMessage
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                        >
                            <FaCheckCircle />
                            Thank you! Your message has been sent successfully.
                        </SuccessMessage>
                    )}

                    <SubmitButton 
                        type="submit" 
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {isSubmitting ? (
                            <>Sending...</>
                        ) : (
                            <>
                                Send Message
                                <FaPaperPlane />
                            </>
                        )}
                    </SubmitButton>
                </ContactForm>

                <ContactInfo variants={itemVariants}>
                    <InfoHeader>
                        <InfoTitle>Contact Information</InfoTitle>
                        <InfoSubtitle>
                            Get in touch with us through any of the following channels
                        </InfoSubtitle>
                    </InfoHeader>

                    <InfoList>
                        <InfoItem
                            whileHover={{ x: 8 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <InfoIcon>
                                <FaMapMarkerAlt />
                            </InfoIcon>
                            <InfoContent>
                                <InfoItemTitle>Our Office</InfoItemTitle>
                                <InfoText>
                                    109/421, 80 feet road<br />
                                    Kanpur, Uttar Pradesh, India
                                </InfoText>
                            </InfoContent>
                        </InfoItem>

                        <InfoItem
                            whileHover={{ x: 8 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <InfoIcon>
                                <FaPhone />
                            </InfoIcon>
                            <InfoContent>
                                <InfoItemTitle>Phone Numbers</InfoItemTitle>
                                <InfoText>
                                    +91-9336652601<br />
                                    +91-9519510543
                                </InfoText>
                            </InfoContent>
                        </InfoItem>

                        <InfoItem
                            whileHover={{ x: 8 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <InfoIcon>
                                <FaEnvelope />
                            </InfoIcon>
                            <InfoContent>
                                <InfoItemTitle>Email Address</InfoItemTitle>
                                <InfoText>
                                    shivnaresh@gmail.com
                                </InfoText>
                            </InfoContent>
                        </InfoItem>

                        <InfoItem
                            whileHover={{ x: 8 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <InfoIcon>
                                <FaClock />
                            </InfoIcon>
                            <InfoContent>
                                <InfoItemTitle>Business Hours</InfoItemTitle>
                                <InfoText>
                                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                                    Saturday: 10:00 AM - 2:00 PM<br />
                                    Sunday: Closed
                                </InfoText>
                            </InfoContent>
                        </InfoItem>
                    </InfoList>
                </ContactInfo>
            </ContactGrid>

          
        </PageContainer>
    );
};

export default Contact;
